import { useEffect, useRef } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import AuthPage from "./features/auth/screens/AuthPageScreen";
import SettingsScreen from "./features/auth/screens/SettingsScreen";
import { AddGifteeWizard } from "./features/giftees";
import GifteeHub from "./features/giftees/screens/GifteeHubScreen";
import GifteeHome from "./features/giftees/screens/GifteeHomeScreen";
import GifteeAllGifteesScreen from "./features/giftees/screens/GifteeAllGifteesScreen";
import GifteeAllOccasionsScreen from "./features/giftees/screens/GifteeAllOccasionsScreen";
import GifteeProfile from "./features/giftees/screens/GifteeProfileScreen";
import GifteeRecoLoadingScreen from "./features/giftees/screens/GifteeRecoLoadingScreen";
import GifteeSwipeRecommendations from "./features/giftees/screens/GifteeSwipeRecommendationsScreen";
import ProtectedRoute from "./components/ProtectedRoute";
import useViewportInsets from "./hooks/useViewportInsets";

function App() {
  const touchStartRef = useRef(null);

  // Publish live viewport/keyboard CSS variables (--gg-keyboard-inset etc.) so
  // fixed bottom CTAs can ride above the iOS software keyboard app-wide.
  useViewportInsets();

  useEffect(() => {
    const onTouchStart = (event) => {
      const point = event.touches?.[0];
      if (!point) return;
      touchStartRef.current = { x: point.clientX, y: point.clientY };
    };

    const onTouchMove = (event) => {
      const point = event.touches?.[0];
      const start = touchStartRef.current;
      if (!point || !start) return;
      const dx = point.clientX - start.x;
      const dy = point.clientY - start.y;
      const isHorizontalPan = Math.abs(dx) > Math.abs(dy);
      const targetEl = event.target instanceof Element ? event.target : null;
      const horizontalPanContainer = targetEl?.closest("[data-horizontal-pan='true']");
      if (!isHorizontalPan) return;
      if (horizontalPanContainer) {
        const maxScrollLeft = Math.max(
          0,
          horizontalPanContainer.scrollWidth - horizontalPanContainer.clientWidth,
        );
        const atLeftEdge = horizontalPanContainer.scrollLeft <= 0.5;
        const atRightEdge = Math.abs(maxScrollLeft - horizontalPanContainer.scrollLeft) <= 0.5;
        const swipingRight = dx > 0;
        const swipingLeft = dx < 0;
        if ((atLeftEdge && swipingRight) || (atRightEdge && swipingLeft)) {
          event.preventDefault();
        }
        return;
      }
      event.preventDefault();
    };

    const clearTouchStart = () => {
      touchStartRef.current = null;
    };

    document.addEventListener("touchstart", onTouchStart, { capture: true, passive: true });
    document.addEventListener("touchmove", onTouchMove, { capture: true, passive: false });
    document.addEventListener("touchend", clearTouchStart, { capture: true, passive: true });
    document.addEventListener("touchcancel", clearTouchStart, { capture: true, passive: true });

    return () => {
      document.removeEventListener("touchstart", onTouchStart, true);
      document.removeEventListener("touchmove", onTouchMove, true);
      document.removeEventListener("touchend", clearTouchStart, true);
      document.removeEventListener("touchcancel", clearTouchStart, true);
    };
  }, []);

  return (
    <Routes>
      {/* Auth — full-screen, no navbar / footer */}
      <Route path="/" element={<AuthPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/signin" element={<Navigate to="/auth" replace />} />
      <Route path="/signup" element={<Navigate to="/auth" replace />} />

      {/* Add Giftee wizard — full-screen, own header, requires auth */}
      <Route
        path="/create-giftee"
        element={
          <ProtectedRoute>
            <AddGifteeWizard />
          </ProtectedRoute>
        }
      />

      {/* Giftee profile (Figma) — full-screen; same component as /edit (must be before /giftee/:gifteeId) */}
      <Route
        path="/giftee/:gifteeId/edit"
        element={
          <ProtectedRoute>
            <GifteeProfile />
          </ProtectedRoute>
        }
      />

      <Route
        path="/giftee/:gifteeId/info"
        element={
          <ProtectedRoute>
            <GifteeProfile />
          </ProtectedRoute>
        }
      />

      {/* Post-login home (Figma Start 106:2400) — aggregate dashboard */}
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <GifteeHome />
          </ProtectedRoute>
        }
      />
      <Route path="/start" element={<Navigate to="/home" replace />} />

      {/* All giftees list — Figma 107:2563 */}
      <Route
        path="/all-giftees"
        element={
          <ProtectedRoute>
            <GifteeAllGifteesScreen />
          </ProtectedRoute>
        }
      />

      {/* All occasions list — Figma 484:10631 */}
      <Route
        path="/all-occasions"
        element={
          <ProtectedRoute>
            <GifteeAllOccasionsScreen />
          </ProtectedRoute>
        }
      />

      {/* Settings — account actions */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsScreen />
          </ProtectedRoute>
        }
      />

      {/* Giftee hub — reminders & recommendations (full-screen, requires auth) */}
      <Route
        path="/giftee/:gifteeId"
        element={
          <ProtectedRoute>
            <GifteeHub />
          </ProtectedRoute>
        }
      />

      <Route
        path="/giftee/:gifteeId/more-recommendations/loading"
        element={
          <ProtectedRoute>
            <GifteeRecoLoadingScreen />
          </ProtectedRoute>
        }
      />

      <Route
        path="/giftee/:gifteeId/more-recommendations"
        element={
          <ProtectedRoute>
            <GifteeSwipeRecommendations />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default App;
